/** @jsx jsx */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { css, jsx } from '@compiled/react';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import { Box, xcss } from '@atlaskit/primitives';
import { SuggestRelatedWorkItemsContentContainer } from '@atlassian/jira-ai-related-issues/src/index.tsx';
import type {
	FormFieldValues,
	GeneralFieldValue,
} from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';

import useIsAdminHubAIEnabled from '@atlassian/jira-platform-react-hooks-use-is-admin-hub-ai-enabled/src/controllers/index.tsx';
import { useProductName } from '@atlassian/jira-tenant-context-controller/src/components/product-name/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIssueCreateModalWithPrefillingForIssueCreateEmbed } from '@atlassian/jira-new-issue-create-trigger/src/index.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useOpenIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/main.tsx';
import {
	type RelatedIssue,
	JiraIssueRelatedIssuesContainer,
} from '@atlassian/jira-ai-related-issues/src/controllers/context.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { getProductForAdminHubAi, transformTheIssueDataToGICFormat } from './utils.tsx';
import messages from './messages.tsx';

type Props = {
	getFormState: () => FormFieldValues;
	setFieldValue: (name: string, value: GeneralFieldValue) => void;
	selectedProjectId?: string;
};

type LinkRelatedIssuePayload = {
	label: string;
	value: string;
	id: string;
	iconUrl: string;
};

const getCustomRelatedIssueSearchPayload = (selectedProjectId: string, summary: string) => {
	return {
		project_id: selectedProjectId,
		issue_summary: summary,
	};
};

export const SimilarWorkItemInGIC = (props: Props) => {
	const summary = props.getFormState()?.values?.summary || '';
	const linkedWorkItems = props.getFormState()?.values?.issuelinks?.issues || [];
	const linkedWorkItemValues = linkedWorkItems.map(
		(linkedWorkItem: LinkRelatedIssuePayload) => linkedWorkItem.value,
	);
	const selectedProjectId = props.selectedProjectId || '';

	const isLinkedWorkItemFieldPresent = props.getFormState()?.values?.issuelinks || null;
	const [showComponent, setShowComponent] = useState(false);
	const [debouncedSummary, setDebouncedSummary] = useState('');
	const prevSummaryRef = useRef(summary);

	const productName = useProductName();
	const adminHubAiProductName = getProductForAdminHubAi(productName);

	const { data: isAdminHubAiEnabled, fetchAIEnablement } =
		useIsAdminHubAIEnabled(adminHubAiProductName);

	const getPrefilledIssueCreateProps = useIssueCreateModalWithPrefillingForIssueCreateEmbed();
	const openIssueCreateModal = useOpenIssueCreateModal();

	// check if AI is enabled for the selected project/product
	useEffect(() => {
		fetchAIEnablement();
	}, [fetchAIEnablement]);

	const debouncedUpdate = useMemo(
		() =>
			debounce((value) => {
				setDebouncedSummary(value);
				if (value) {
					setShowComponent(true);
				} else {
					setShowComponent(false);
				}
			}, 1000),
		[],
	);

	useEffect(() => {
		const trimmedSummary = String(summary).trim();

		if (trimmedSummary !== prevSummaryRef.current) {
			debouncedUpdate(trimmedSummary);
			prevSummaryRef.current = trimmedSummary;
		}
		return () => {
			debouncedUpdate.cancel();
		};
	}, [summary, debouncedUpdate]);

	const customRelatedIssueSearchPayload = useMemo(() => {
		return getCustomRelatedIssueSearchPayload(selectedProjectId, debouncedSummary);
	}, [selectedProjectId, debouncedSummary]);

	const prefillIssueCreateModalWithData = async (transformedPayload: Record<string, unknown>) => {
		const { issueCreatePayload, prefilledCustomFields, ecosystemCallback } =
			await getPrefilledIssueCreateProps(transformedPayload);

		// Open the issue creation modal with the necessary payload
		openIssueCreateModal({
			triggerPointKey: 'global-create-button',
			payload: {
				...issueCreatePayload,
			},
			prefilledCustomFields,
			ecosystemCallback,
		});
	};

	const onCopyButtonClick = async (issueKey: string): Promise<void> => {
		try {
			// Fetch issue details from the API
			const response = await performGetRequest(`/rest/api/2/issue/${issueKey}`);

			// Transform the response fields into a usable payload
			const transformedPayload = transformTheIssueDataToGICFormat(response?.fields);

			// Get prefilled issue creation properties
			prefillIssueCreateModalWithData(transformedPayload);
		} catch (error: unknown) {
			// Log and fire error analytics
			fireErrorAnalytics({
				error: error instanceof Error ? error : new Error('Unknown error'),
				meta: {
					id: 'getIssueFieldsSimilarWorkItemsInGIC',
					packageName: '@atlassian/jira-issue-create',
					teamName: 'GRAVITY',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	};

	const onLinkIssueButtonClick = (relatedIssue: RelatedIssue): void => {
		const linkRelatedIssuePayload: LinkRelatedIssuePayload = {
			label: relatedIssue.issueKey,
			value: relatedIssue.issueKey,
			id: relatedIssue.id,
			iconUrl: relatedIssue.issueTypeIconUrl,
		};

		const currentLinking = props.getFormState()?.values?.issuelinks.linkType || null;
		const currentLinkedIssues = props.getFormState()?.values?.issuelinks.issues || [];
		if (currentLinking) {
			props.setFieldValue('issuelinks', {
				linkType: currentLinking,
				issues: [...currentLinkedIssues, linkRelatedIssuePayload],
			});
		}
	};

	return fg('similar_items_in_gic_ui_fixes')
		? showComponent && isAdminHubAiEnabled && (
				<div css={similarWorkItemsWrapperStyles}>
					<Box xcss={relatedIssuesContainerStylesUIFix}>
						<JiraIssueRelatedIssuesContainer scope="related-issues-in-gic">
							<SuggestRelatedWorkItemsContentContainer
								issueKey=""
								onIssueLinkSave={noop}
								existingIssues={
									fg('similar_work_items_in_gic_link_work_items') ? linkedWorkItemValues : ['']
								}
								customRelatedIssueSearchPayload={customRelatedIssueSearchPayload}
								invokedFromSource="globalIssueCreate"
								headerTitle={messages.similarWorkItems}
								{...(expVal('similar_work_items_copy_field_milestone-2', 'isEnabled', false)
									? { onCopyButtonClick }
									: undefined)}
								{...(fg('similar_work_items_in_gic_link_work_items')
									? {
											onSimilarIssueLinkGICButtonClick: isLinkedWorkItemFieldPresent
												? onLinkIssueButtonClick
												: null,
										}
									: {})}
							/>
						</JiraIssueRelatedIssuesContainer>
					</Box>
				</div>
			)
		: showComponent && isAdminHubAiEnabled && (
				<Box xcss={relatedIssuesContainerStyles}>
					<JiraIssueRelatedIssuesContainer scope="related-issues-in-gic">
						<SuggestRelatedWorkItemsContentContainer
							issueKey=""
							onIssueLinkSave={noop}
							existingIssues={
								fg('similar_work_items_in_gic_link_work_items') ? linkedWorkItemValues : ['']
							}
							customRelatedIssueSearchPayload={customRelatedIssueSearchPayload}
							invokedFromSource="globalIssueCreate"
							headerTitle={messages.similarWorkItems}
							{...(expVal('similar_work_items_copy_field_milestone-2', 'isEnabled', false)
								? { onCopyButtonClick }
								: undefined)}
							{...(fg('similar_work_items_in_gic_link_work_items')
								? {
										onSimilarIssueLinkGICButtonClick: isLinkedWorkItemFieldPresent
											? onLinkIssueButtonClick
											: null,
									}
								: {})}
						/>
					</JiraIssueRelatedIssuesContainer>
				</Box>
			);
};

const relatedIssuesContainerStylesUIFix = xcss({
	marginTop: 'space.050',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border.input',
	borderRadius: 'border.radius',
});

const relatedIssuesContainerStyles = xcss({
	marginTop: 'space.300',
});

const similarWorkItemsWrapperStyles = css({
	zIndex: 0,
	position: 'relative',
});
